import { MarketAccountSelectionList } from '@src/components/MarketAccountSelectionList';
import { useSecuritiesContext } from '@src/providers';
import { HStack, isMultileg, useCallbackRef, useMarketAccountsContext } from '@talos/kyoko';
import { useCallback, useMemo, useState, type RefObject } from 'react';
import { useTheme } from 'styled-components';
import { v1 as uuid } from 'uuid';

import { MARKET_DATA_CARD_EXCLUDED_PRODUCT_TYPES } from './MarketDataCardSymbolSelector';
import { MarketAccountsWrapper, SymbolSelector } from './styles';

import {
  Box,
  Button,
  ButtonActions,
  ButtonVariants,
  CardViewEnum,
  ConnectionType,
  Divider,
  EMPTY_ARRAY,
  FormControlSizes,
  FormGroup,
  FormMessage,
  IconButton,
  IconName,
  Input,
  MarketDataCardFormWrapper,
  MarketDataCardState,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  NotificationVariants,
  ProductTypeEnum,
  Toggle,
  ToggleGroup,
  UnifiedLiquidityEnum,
  useConstant,
  useGlobalToasts,
  useMarketsContext,
  useMixpanel,
  useSecurity,
  useUnifiedLiquidity,
  useUnifiedLiquidityParameter,
  useUserContext,
  type MarketDataCardConfig,
} from '@talos/kyoko';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { useNavigateToMultilegCombo } from '../../hooks/useNavigateToMultilegCombo';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { getRestFailureUserMessage } from '../../utils/rtkQuery/restFailureError';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import { MarketSelector } from '../MarketSelector/MarketSelector';
import { UnifiedLiquidityMarketTooltipContent } from '../MarketSelector/UnifiedLiquidityMarketTooltipContent';
import { useUnifiedLiquidityMarketSelectorExtensionFactory } from '../MarketSelector/extensions/useUnifiedLiquidityMarketSelectorExtension';
import { getSelectedUnifiedLiquidityMarketTooltipGroups } from '../MarketSelector/selectedUnifiedLiquidityMarketTooltip';
import { useULSymbolsVisibility } from '../MarketSelector/useULSymbolsVisibility';
import { getSelectedMarketAccountsWithoutUnifiedLiquiditySymbols } from '../MarketSelector/utils/unifiedLiquidity';
import { MultiLegSummary } from '../MultiLegSummary';
import { MultilegComboType } from '../MultilegCombo/enums';
import { getMultilegInstrumentType } from '../OMS/NewOrder/utils/mappers';
import { useSymbolSelectorWidth } from '../SymbolSelector/useSymbolSelectorWidth';
import { SettingsRelatedActionsButton } from './SettingsRelatedActionsButton';
import { getMultilegUnifiedLiquidityMarketAccounts, updateMultilegLegMarkets } from './multilegLegMarketAccounts';
import { useMarketDataCardSettings } from './useMarketDataCardSettings';

interface MarketDataCardSettingsProps {
  config: MarketDataCardConfig;
  onClose: () => void;
  saveCard: (config: MarketDataCardConfig) => void;
  deleteCard: (config: MarketDataCardConfig) => void;
  hidePlaceholderCards: () => void;
  cardRef: RefObject<HTMLDivElement>;
}

export function MarketDataCardSettings({
  config,
  onClose,
  saveCard,
  deleteCard,
  hidePlaceholderCards,
  cardRef,
}: MarketDataCardSettingsProps) {
  const { useNewMarketSelector } = useTradingSettings();
  const mixpanel = useMixpanel();
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useUserContext();
  const { spacing4 } = useTheme();
  const dropdownWidth = useSymbolSelectorWidth();
  const { isPopout } = usePanelContext();
  const isInMobileMode = useIsMobileLayout();
  const navigateToMultilegCombo = useNavigateToMultilegCombo();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    form,
    legMarketAccounts,
    ulEnabled,
    enableShowBBO,
    setShowBBO,
    openMarketAccountSelector,
    onSymbolChange,
    updateForm,
    marketSelector,
    marketSelectorItems,
    oldMarketSelectorItems,
    onChangeUnifiedLiquidity,
    onMultilegULMarketsChange,
    onLegMarketAccountsChange,
    onMarketAccountsChange,
    onMarketsChange,
  } = useMarketDataCardSettings({ config });

  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const security = useSecurity(form.symbol);
  // Default the state to UPDATED_CARD for... some reason. Maybe backwards compat from a long time ago.
  const { state = MarketDataCardState.UPDATED_CARD } = config;

  const unifiedLiquidityToken = useUnifiedLiquidity(form.symbol);
  const showUnifiedLiquidityOption = !!unifiedLiquidityToken;
  const unifiedLiquidityTooltipGroups = useMemo(
    () =>
      security && unifiedLiquidityToken
        ? getSelectedUnifiedLiquidityMarketTooltipGroups({
            primarySymbol: security.Symbol,
            primarySecurity: security,
            unifiedLiquidityTokens: unifiedLiquidityToken.Tokens,
            selectedMarkets: form.selectedMarkets,
            selectedMarketAccounts: form.selectedMarketAccounts,
            selectedUnifiedLiquidityMarketSymbols: ulEnabled ? form.unifiedLiquidityMarketSymbols : EMPTY_ARRAY,
            marketAccountsByMarket,
            marketAccountsByName,
            marketDisplayNameByName,
            securitiesBySymbol,
          })
        : EMPTY_ARRAY,
    [
      form.selectedMarketAccounts,
      form.selectedMarkets,
      form.unifiedLiquidityMarketSymbols,
      ulEnabled,
      marketAccountsByMarket,
      marketAccountsByName,
      marketDisplayNameByName,
      securitiesBySymbol,
      security,
      unifiedLiquidityToken,
    ]
  );
  const { enableUnifiedLiquidityMarketSymbols, unifiedLiquidityCombinePerpsAndCFDs } = useFeatureFlag();
  const [ulSymbolsVisible, setUlSymbolsVisible, syncFromUnifiedLiquidity] = useULSymbolsVisibility(
    ulEnabled,
    form.symbol
  );
  const handleUnifiedLiquidityToggle = useCallbackRef((enabled: boolean) => {
    onChangeUnifiedLiquidity(enabled);
    syncFromUnifiedLiquidity(enabled);
  });
  const unifiedLiquidityExtensionFactory = useUnifiedLiquidityMarketSelectorExtensionFactory({
    enabled: enableUnifiedLiquidityMarketSymbols && ulSymbolsVisible,
    onEnabledChange: setUlSymbolsVisible,
    showToggle: enableUnifiedLiquidityMarketSymbols && showUnifiedLiquidityOption,
    items: marketSelectorItems,
    symbol: security?.Symbol,
    unifiedLiquidityCombinePerpsAndCFDs,
    selectionTarget: {
      type: 'external',
      selections: form.unifiedLiquidityMarketSymbols,
      onChange: unifiedLiquidityMarketSymbols => updateForm({ unifiedLiquidityMarketSymbols }),
    },
    showSelectionListTooltip: enableUnifiedLiquidityMarketSymbols && ulEnabled,
  });
  const marketSelectorExtensions = useMemo(
    () => (unifiedLiquidityExtensionFactory ? [unifiedLiquidityExtensionFactory] : []),
    [unifiedLiquidityExtensionFactory]
  );
  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      setIsSubmitting(true);
      e.preventDefault();

      let newConfig: MarketDataCardConfig = {
        ...config,
        ...form,
        // After setting a new config, all markets are selected by default for the 3rd slide
        availableMarketAccounts: [...form.selectedMarketAccounts],
        selectedMarketAccounts: [...form.selectedMarketAccounts],
        availableMarkets: [...(form.selectedMarkets ?? [])],
        selectedMarkets: [...(form.selectedMarkets ?? [])],
        selectedAggregation: form.selectedAggregation,
        unifiedLiquidityMarketSymbols: form.unifiedLiquidityMarketSymbols,
        detailsExpanded: security?.ProductType === ProductTypeEnum.Spot ? false : config.detailsExpanded,
        unifiedLiquidity: ulEnabled ? UnifiedLiquidityEnum.Enabled : UnifiedLiquidityEnum.Disabled,
        alwaysDisplayBestBidOffer: enableShowBBO,
      };

      if (newConfig.state == null) {
        newConfig.state = MarketDataCardState.UPDATED_CARD;
      }

      if (securitiesBySymbol && config.symbol !== form.symbol) {
        const security = form.symbol != null && securitiesBySymbol.get(form.symbol);
        if (security) {
          newConfig = {
            ...newConfig,
            id: config.id || uuid(),
            priceIncrement: security.DefaultPriceIncrement,
            sizeBuckets: security.SizeBuckets,
            symbol: security.Symbol,
            orderPresetID: undefined,
            topOfBookSizeBucket: security.NormalSize,
          };
        }
      }
      // Track usage of unified liquidity card
      if (
        // Unified liquidity was updated
        config.unifiedLiquidity !== newConfig.unifiedLiquidity &&
        newConfig.unifiedLiquidity !== undefined
      ) {
        mixpanel.track(MixpanelEvent.SetMarketCardUnifiedLiquidity, {
          [MixpanelEventProperty.Enabled]: newConfig.unifiedLiquidity,
        });
      }
      // Track usage of titles
      if (newConfig.title?.length && config.title !== newConfig.title) {
        mixpanel.track(MixpanelEvent.SetMarketCardTitle);
      }
      // Track usage of compact view
      if (config.view !== newConfig.view) {
        mixpanel.track(MixpanelEvent.SetMarketCardView, {
          [MixpanelEventProperty.View]: newConfig.view,
        });
      }

      try {
        const updatedMultilegLegMarkets = await updateMultilegLegMarkets({
          security,
          legMarketAccounts,
          orgApiEndpoint,
        });

        if (updatedMultilegLegMarkets && security) {
          addToast({
            text: `Symbol ${security.DisplaySymbol ?? security.Symbol} updated.`,
            variant: NotificationVariants.Positive,
          });
        }

        saveCard(newConfig);
        onClose();
      } catch (error) {
        addToast({
          text: getRestFailureUserMessage(error) ?? 'Unable to update multileg leg markets.',
          variant: NotificationVariants.Negative,
        });
        setIsSubmitting(false);
      }
    },
    [
      config,
      addToast,
      form,
      legMarketAccounts,
      mixpanel,
      onClose,
      saveCard,
      securitiesBySymbol,
      security,
      ulEnabled,
      enableShowBBO,
      orgApiEndpoint,
    ]
  );

  const invalidMarketAccounts = form.selectedMarketAccounts.length === 0;
  const invalidMarkets = form.selectedMarkets == null || form.selectedMarkets.length === 0;
  const invalidUnifiedLiquidityMarkets =
    (invalidMarketAccounts && invalidMarkets) ||
    getSelectedMarketAccountsWithoutUnifiedLiquiditySymbols({
      selectedMarketAccounts: form.selectedMarketAccounts,
      marketSymbols: form.unifiedLiquidityMarketSymbols,
    }).length > 0;
  const usesStructuredUnifiedLiquidityMarkets =
    enableUnifiedLiquidityMarketSymbols && ulEnabled && !isMultileg(security);
  const unifiedLiquidityMarketSelectorProps = usesStructuredUnifiedLiquidityMarkets
    ? {
        selectedUnifiedLiquidityMarkets: form.unifiedLiquidityMarketSymbols,
        unifiedLiquidityTokens: unifiedLiquidityToken?.Tokens,
        onChangeUnifiedLiquidityMarkets: (unifiedLiquidityMarketSymbols: typeof form.unifiedLiquidityMarketSymbols) =>
          updateForm({ unifiedLiquidityMarketSymbols }),
      }
    : {};
  const primaryButtonDisabled =
    isSubmitting ||
    security == null ||
    (usesStructuredUnifiedLiquidityMarkets ? invalidUnifiedLiquidityMarkets : invalidMarketAccounts && invalidMarkets);

  const handleDeleteCard = useCallback(() => {
    deleteCard(config);
    if (state === MarketDataCardState.NEW_CARD) {
      hidePlaceholderCards();
    }
  }, [config, state, deleteCard, hidePlaceholderCards]);

  const handleOpenMarketSelector = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.OpenMarketSelectorFromMarketCardSettings, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCard,
    });
  });

  const symbolSelectorStartOpen = useConstant(form.symbol == null);
  const unifiedLiquidityParameter = useUnifiedLiquidityParameter();
  const handleOpenEditMultileg = useCallbackRef((_symbol: string) => {
    if (!security) {
      return;
    }
    mixpanel.track(MixpanelEvent.EditMultileg, {
      [MixpanelEventProperty.Source]: MixpanelEventSource.MarketCard,
    });
    navigateToMultilegCombo({
      instrumentType:
        getMultilegInstrumentType(security.MultilegDetails?.SyntheticProductType) ?? MultilegComboType.SyntheticCross,
      security,
      isEdit: true,
    });
  });

  const multilegULLegMarketAccounts = useMemo(
    () => getMultilegUnifiedLiquidityMarketAccounts(form.selectedMarketAccounts),
    [form.selectedMarketAccounts]
  );

  return (
    <>
      <MarketDataCardFormWrapper onSubmit={handleSubmit}>
        <FormGroup
          size={FormControlSizes.Small}
          label="Symbol"
          inline
          mb="spacing8"
          labelWidth="6rem"
          alignItems="center"
        >
          <Box data-testid="market-card-settings-symbol-selector">
            <SymbolSelector
              symbol={form.symbol}
              onSymbolChanged={onSymbolChange}
              startOpen={symbolSelectorStartOpen}
              size={FormControlSizes.Small}
              dropdownWidth={dropdownWidth}
              dropdownPlacement="bottom-end"
              showGroups
              showFavorites
              portalize
              excludeProductTypes={MARKET_DATA_CARD_EXCLUDED_PRODUCT_TYPES}
            />
          </Box>
          {state === MarketDataCardState.UPDATED_CARD && (
            <IconButton
              ghost
              icon={IconName.Close}
              onClick={onClose}
              style={{ marginLeft: spacing4 }}
              size={FormControlSizes.Small}
              data-testid="market-card-settings-slide-close"
            />
          )}
        </FormGroup>
        <FormGroup size={FormControlSizes.Small} label="Title" inline mb="spacing8" controlId="title" labelWidth="6rem">
          <Input
            id="title"
            name="title"
            value={form.title ?? ''}
            placeholder="Custom card title (optional)"
            size={FormControlSizes.Small}
            onChange={e => updateForm({ title: e.target.value })}
          />
        </FormGroup>
        {!isInMobileMode && (
          <FormGroup
            size={FormControlSizes.Small}
            label="Card View"
            inline
            mb="spacing8"
            controlId="title"
            labelWidth="6rem"
          >
            <ToggleGroup<CardViewEnum>
              size={FormControlSizes.Small}
              options={[
                {
                  value: CardViewEnum.NORMAL,
                  displayName: 'Normal',
                  buttonVariant: ButtonVariants.Priority,
                  testId: 'market-card-settings-card-view-normal',
                },
                {
                  value: CardViewEnum.COMPACT,
                  displayName: 'Compact',
                  buttonVariant: ButtonVariants.Priority,
                  testId: 'market-card-settings-card-view-compact',
                },
              ]}
              value={form.view}
              onChange={view => updateForm({ view })}
              boxProps={{ flex: '1' }}
              data-testid="market-card-settings-view-toggle"
            />
          </FormGroup>
        )}
        <HStack gap="spacing8" justifyContent="space-between">
          <FormGroup
            size={FormControlSizes.Small}
            label="Show BBO"
            tooltip="Always show the Best Bid Offer rate at the top of the market card, regardless of the entered Qty."
            inline
            mb="spacing8"
            controlId="title"
            labelWidth="4rem"
            alignItems="center"
          >
            <Toggle
              size={FormControlSizes.Default}
              checked={enableShowBBO}
              onChange={(value: boolean) => setShowBBO(value)}
            />
          </FormGroup>
          {security && form.symbol && showUnifiedLiquidityOption && unifiedLiquidityParameter && (
            <FormGroup
              size={FormControlSizes.Small}
              label={unifiedLiquidityParameter.DisplayName}
              tooltip={
                ulEnabled && unifiedLiquidityTooltipGroups.length ? (
                  <UnifiedLiquidityMarketTooltipContent
                    groups={unifiedLiquidityTooltipGroups}
                    dataTestId="market-card-settings-unified-liquidity-tooltip"
                    statusTestIdPrefix="market-card-settings-unified-liquidity-tooltip-status"
                  />
                ) : (
                  unifiedLiquidityParameter.Description
                )
              }
              tooltipProps={ulEnabled && unifiedLiquidityTooltipGroups.length ? { isSmall: false } : undefined}
              inline
              mb="spacing8"
              controlId="title"
              labelWidth="6rem"
              alignItems="center"
            >
              <Toggle
                size={FormControlSizes.Default}
                checked={ulEnabled}
                onChange={handleUnifiedLiquidityToggle}
                data-testid="market-card-settings-unified-liquidity-toggle"
              />
            </FormGroup>
          )}
        </HStack>

        {security ? (
          isMultileg(security) ? (
            <MarketAccountsWrapper>
              <MultiLegSummary
                security={security}
                readOnly={false}
                supportsAllMarkets={true}
                touched={true}
                legMarkets={legMarketAccounts}
                unifiedLiquidityToken={ulEnabled ? unifiedLiquidityToken : undefined}
                unifiedLiquidityMarkets={multilegULLegMarketAccounts}
                onEditSymbol={handleOpenEditMultileg}
                onLegMarketsChange={onLegMarketAccountsChange}
                onUnifiedLiquidityMarketsChange={onMultilegULMarketsChange}
                isMarketSelectorNarrow={isPopout || isInMobileMode}
                allocations={-1}
                enableInPanelMarketSelectorLegSelection
              />
            </MarketAccountsWrapper>
          ) : useNewMarketSelector ? (
            <>
              <Divider orientation="horizontal" mb="spacing8" />
              <MarketSelector
                size={FormControlSizes.Small}
                marketSelectorAPI={marketSelector}
                forceOpen={openMarketAccountSelector}
                onOpen={handleOpenMarketSelector}
                showCredentialsOnlyToggle
                panelOffset={8}
                panelPlacement="auto"
                panelAnchorRef={cardRef}
                isNarrow={isPopout || isInMobileMode}
                extensions={marketSelectorExtensions}
              />
              {usesStructuredUnifiedLiquidityMarkets && invalidUnifiedLiquidityMarkets && (
                <FormMessage>Select at least one UL symbol for each selected market account.</FormMessage>
              )}
            </>
          ) : (
            <MarketAccountsWrapper>
              <MarketAccountSelectionList
                availableMarkets={oldMarketSelectorItems.markets}
                availableMarketAccounts={oldMarketSelectorItems.marketAccounts}
                selectedMarketAccounts={form.selectedMarketAccounts}
                selectedMarkets={form.selectedMarkets}
                {...unifiedLiquidityMarketSelectorProps}
                security={security}
                onChangeMarketAccounts={onMarketAccountsChange}
                onChangeMarkets={onMarketsChange}
                connectionType={ConnectionType.MarketData}
                showInactiveMarketAccounts={true}
              />
              {usesStructuredUnifiedLiquidityMarkets && invalidUnifiedLiquidityMarkets && (
                <FormMessage>Select at least one UL symbol for each selected market account.</FormMessage>
              )}
            </MarketAccountsWrapper>
          )
        ) : (
          <Box flex="1" />
        )}

        <ButtonActions gap="spacing8">
          <SettingsRelatedActionsButton
            selectedMarketAccounts={form.selectedMarketAccounts}
            selectedMarkets={form.selectedMarkets}
            symbol={form.symbol}
          />

          <Button
            size={FormControlSizes.Small}
            onClick={handleDeleteCard}
            variant={state === MarketDataCardState.NEW_CARD ? ButtonVariants.Priority : ButtonVariants.Negative}
            data-testid="market-data-card-settings-delete-button"
            flex="1"
          >
            {state === MarketDataCardState.NEW_CARD ? `Cancel` : `Delete Card`}
          </Button>
          <Button
            size={FormControlSizes.Small}
            variant={state === MarketDataCardState.NEW_CARD ? ButtonVariants.Positive : ButtonVariants.Primary}
            type="submit"
            disabled={primaryButtonDisabled}
            data-testid="market-data-card-settings-update-button"
            flex="1"
          >
            {isSubmitting
              ? state === MarketDataCardState.NEW_CARD
                ? `Creating...`
                : `Saving...`
              : state === MarketDataCardState.NEW_CARD
                ? `Create a Card`
                : `Update`}
          </Button>
        </ButtonActions>
      </MarketDataCardFormWrapper>
    </>
  );
}
